<template>
  <div
    class="range-wrapper"
    :style="{
      '--thumb-min': min_percentage,
      '--thumb-max': max_percentage,
    }"
  >
    <div v-if="items" class="range-slider">
      <div v-if="inputs" class="range-number-input">
        <input
          type="number"
          :min="min_angle"
          :max="max_angle"
          step="1"
          :value="slider_min"
          @change="(e) => handle_slider_min_input(e, false)"
        />
        <input
          type="number"
          :min="min_angle"
          :max="max_angle"
          step="1"
          :value="slider_max"
          @change="(e) => handle_slider_max_input(e, false)"
        />
      </div>

      <div v-if="scale.length > 1" class="range-bars">
        <div
          v-for="(item, index) in scale"
          :key="index"
          v-b-popover.hover.top="bar_label(item)"
          class="range-bar-wrapper"
          :style="{ height: get_height(item) }"
        >
          <div
            :class="is_bar_selected(index) ? 'range-bar selected' : 'range-bar'"
          ></div>
        </div>
      </div>

      <div class="range-slider-input" @click.prevent="handle_click">
        <input
          ref="min"
          type="range"
          :min="min_angle"
          :max="max_angle"
          :value="slider_min"
          step="1"
          @input.prevent="(e) => handle_slider_min_input(e, true)"
          @change.prevent="(e) => handle_slider_min_input(e, false)"
        />

        <input
          ref="max"
          type="range"
          :min="min_angle"
          :max="max_angle"
          :value="slider_max"
          step="1"
          @input.prevent="(e) => handle_slider_max_input(e, true)"
          @change.prevent="(e) => handle_slider_max_input(e, false)"
        />
      </div>
    </div>

    <div v-if="vb_options.debug">
      <br /><br />
      PROPS<br />
      min: {{ vb_options.min }}<br />
      max: {{ vb_options.max }}<br />
      items: {{ items.length }}<br />
      min_angle: {{ vb_options.min_angle }}<br />
      max_angle: {{ vb_options.max_angle }}<br />
      <hr />
      DATA:<br />
      min_angle: {{ min_angle }}<br />
      max_angle: {{ max_angle }}<br />
      slider_min_live: {{ slider_min_live }}<br />
      slider_max_live: {{ slider_max_live }}<br />
      slider_min: {{ slider_min }}<br />
      slider_max: {{ slider_max }}<br />
      items: {{ items.length }}<br />
    </div>
  </div>
</template>

<script>
  import {
    number,
    subtract,
    sum,
    divide,
    multiply,
    floor,
    ceil,
    abs,
    min,
    max,
    round,
    format,
  } from 'mathjs';
  import {
    extend_bounds,
    normalize_min,
    normalize_max,
    highest_min,
    lowest_max,
  } from './range-slider-bounds';

  const is_bound = (value) =>
    value !== null &&
    value !== undefined &&
    value !== '' &&
    !Number.isNaN(Number(value));

  export default {
    name: 'vb-range-slider',
    props: {
      vb_options: {
        type: Object,
        default: () => ({
          min: 0, // default min pre-selezionato
          max: 100, // default max pre-selezionato
          items: [],
          inputs: true,
          allow_click: true,
          min_angle: 0, // bound inferiore intervallo
          max_angle: 0, // bound superiore intervallo
          debug: false,
          threshold: 0,
        }),
      },
    },
    data() {
      return {
        slider_min: 0,
        slider_max: 0,
        slider_min_live: 0,
        slider_max_live: 0,
      };
    },
    computed: {
      items() {
        return this.vb_options.items;
      },
      inputs() {
        return this.vb_options.inputs;
      },
      step() {
        const sized = this.items.find(
          (item) => is_bound(item.min) && is_bound(item.max)
        );
        if (!sized) return 0;
        return subtract(number(sized.max), number(sized.min));
      },
      scale() {
        const { step } = this;
        return (this.items || []).map((item, index) => {
          const has_min = is_bound(item.min);
          const has_max = is_bound(item.max);
          let item_min;
          if (has_min) item_min = number(item.min);
          else if (has_max) item_min = subtract(number(item.max), step);
          else item_min = multiply(index, step);
          let item_max;
          if (has_max) item_max = number(item.max);
          else if (has_min) item_max = sum(number(item.min), step);
          else item_max = multiply(sum(index, 1), step);
          return {
            count: item.count,
            open_start: !has_min,
            open_end: !has_max,
            min: item_min,
            max: item_max,
          };
        });
      },
      bounded() {
        return this.step > 0 && this.scale.length > 0;
      },
      max_item_count() {
        return max(...this.vb_options.items.map(i => i.count));
      },
      raw_min() {
        if (is_bound(this.vb_options.min_angle)) return this.vb_options.min_angle;
        if (this.bounded) return this.scale[0].min;
        return this.vb_options.min;
      },
      raw_max() {
        if (is_bound(this.vb_options.max_angle)) return this.vb_options.max_angle;
        if (this.bounded) return this.scale[this.scale.length - 1].max;
        return this.vb_options.max;
      },
      // unbounded: open buckets extend the track, whose ends mean "unlimited"
      bounds() {
        const unbounded = this.vb_options.unbounded === true && this.bounded;
        return extend_bounds({
          raw_min: number(this.raw_min),
          raw_max: number(this.raw_max),
          step: this.step,
          open_start: unbounded && this.scale[0].open_start,
          open_end: unbounded && this.scale[this.scale.length - 1].open_end,
        });
      },
      min_angle() {
        return this.bounds.min;
      },
      max_angle() {
        return this.bounds.max;
      },
      min_percentage() {
        return `${this.value_to_percentage(this.slider_min_live)}%`;
      },
      max_percentage() {
        return `${this.value_to_percentage(this.slider_max_live)}%`;
      },
    },
    watch: {
      'vb_options.min': 'sync_selection',
      'vb_options.max': 'sync_selection',
      'vb_options.min_angle': 'sync_selection',
      'vb_options.max_angle': 'sync_selection',
    },
    created() {
      this.sync_selection();
    },
    methods: {
      format_value(num) {
        if (!is_bound(num)) return num;
        const n = floor(number(num));
        const formatted = format(n, { notation: 'fixed', precision: 0 });
        return `${formatted.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}€`;
      },
      bar_label(item) {
        if (item.open_start) return `< ${this.format_value(item.max)}`;
        if (item.open_end) return `${this.format_value(item.min)} +`;
        return this.format_value(item.min);
      },
      clamp_to_angles(value, fallback) {
        if (value === undefined || value === null) return fallback;
        const candidate = number(value);
        if (candidate < this.min_angle) return this.min_angle;
        if (candidate > this.max_angle) return this.max_angle;
        return candidate;
      },
      value_to_percentage(value) {
        const range = subtract(this.max_angle, this.min_angle);
        if (range <= 0) return 0;
        let percentage = multiply(
          divide(subtract(value, this.min_angle), range),
          100
        );
        if (percentage < 0) percentage = 0;
        if (percentage > 100) percentage = 100;
        return round(percentage, 2);
      },
      sync_selection() {
        let next_min = this.clamp_to_angles(
          this.vb_options.min,
          this.min_angle
        );
        let next_max = this.clamp_to_angles(
          this.vb_options.max,
          this.max_angle
        );
        if (next_min > next_max) {
          next_min = this.min_angle;
          next_max = this.max_angle;
        }
        this.slider_min = next_min;
        this.slider_min_live = next_min;
        this.slider_max = next_max;
        this.slider_max_live = next_max;
      },
      // a thumb at the track end includes everything beyond it
      is_bar_selected(index) {
        const item = this.scale[index];
        const lower =
          this.slider_min_live <= this.min_angle ? -Infinity : this.slider_min_live;
        const upper =
          this.slider_max_live >= this.max_angle ? Infinity : this.slider_max_live;
        // bucket bounds on the slider integer grid, like inner_min/inner_max
        return ceil(item.min) < upper && floor(item.max) > lower;
      },
      get_height(item) {
        if (item.count === 0) {
          return '0%';
        }

        const min_height = 10;
        const height = item.count / this.max_item_count * 100;

        return `${Math.max(height, min_height)}%`
      },
      // emits min: null when the lower bound is unlimited
      set_min(raw, dragging) {
        const { position, value } = normalize_min(
          min(raw, highest_min(this.scale, this.slider_max)),
          this.slider_max,
          this.bounds
        );
        this.slider_min = position;
        this.slider_min_live = position;
        this.$emit('change', { min: value, dragging });
        return position;
      },
      // emits max: null when the upper bound is unlimited
      set_max(raw, dragging) {
        const { position, value } = normalize_max(
          max(raw, lowest_max(this.scale, this.slider_min)),
          this.slider_min,
          this.bounds
        );
        this.slider_max = position;
        this.slider_max_live = position;
        this.$emit('change', { max: value, dragging });
        return position;
      },
      handle_slider_min_input(event, dragging = false) {
        event.target.value = this.set_min(number(event.target.value), dragging);
      },
      handle_slider_max_input(event, dragging = false) {
        event.target.value = this.set_max(number(event.target.value), dragging);
      },
      handle_click(event) {
        if (this.vb_options.allow_click === false) return;
        event.preventDefault();
        if (event.target.type === 'range') return;
        const input = event.target;
        const rect = input.getBoundingClientRect();
        const click_x = subtract(event.clientX, rect.left);
        const percent = divide(click_x, rect.width);
        const new_value = round(
          sum(
            this.min_angle,
            multiply(percent, subtract(this.max_angle, this.min_angle))
          )
        );
        if (new_value < this.slider_min) {
          this.set_min(new_value, false);
          return;
        }
        if (new_value > this.slider_max) {
          this.set_max(new_value, false);
          return;
        }
        const slider_min_delta = abs(subtract(this.slider_min, new_value));
        const slider_max_delta = abs(subtract(this.slider_max, new_value));
        if (slider_min_delta < slider_max_delta) {
          this.set_min(new_value, false);
          return;
        }

        this.set_max(new_value, false);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .range {
    &-slider {
      margin: auto;
      text-align: center;
      position: relative;
      min-height: 60px;
      //height: 6em;

      input[type='range'] {
        position: absolute;
        left: 0;
        bottom: 0;
        pointer-events: none; /* disabilita tutto */
        &::-webkit-slider-thumb {
          pointer-events: auto; /* abilita solo il drag */
        }
        &::-moz-range-thumb {
          pointer-events: auto; /* abilita solo il drag */
        }
        background-color: transparent;
        @-moz-document url-prefix() {
          /* Firefox only (autoprefixer can't parse @supports selector()) */
          bottom: -11px;
        }
      }

      input[type='number'] {
        border: 1px solid #ddd;
        text-align: center;
        -moz-appearance: textfield;
        appearance: textfield;
      }

      input[type='number']::-webkit-outer-spin-button,
      input[type='number']::-webkit-inner-spin-button {
        -webkit-appearance: none;
      }

      input[type='number']:invalid,
      input[type='number']:out-of-range {
        border: 2px solid var(--secondary);
      }

      input[type='range'] {
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
      }

      input[type='range']:focus {
        outline: none;
      }

      input[type='range']:focus::-webkit-slider-runnable-track {
        background: linear-gradient(
          90deg,
          #ddd var(--thumb-min),
          var(--info) var(--thumb-min),
          var(--info) var(--thumb-max),
          #ddd var(--thumb-max)
        );
      }

      input[type='range']:focus::-moz-range-track {
        background: linear-gradient(
          90deg,
          transparent var(--thumb-min),
          var(--info) var(--thumb-min),
          var(--info) var(--thumb-max),
          transparent var(--thumb-max)
        );
      }

      input[type='range']::-webkit-slider-runnable-track {
        width: 100%;
        height: 3px;
        cursor: pointer;
        background: linear-gradient(
          90deg,
          #ddd var(--thumb-min),
          var(--info) var(--thumb-min),
          var(--info) var(--thumb-max),
          #ddd var(--thumb-max)
        );
        border-radius: 1px;
        box-shadow: none;
        border: 0;
      }

      input[type='range']::-moz-range-track {
        width: 100%;
        height: 3px;
        cursor: pointer;
        background: linear-gradient(
          90deg,
          transparent var(--thumb-min),
          var(--info) var(--thumb-min),
          var(--info) var(--thumb-max),
          transparent var(--thumb-max)
        );
        border-radius: 1px;
        box-shadow: none;
        border: 0;
      }

      input[type='range']::-webkit-slider-thumb {
        z-index: 2;
        position: relative;
        box-shadow: 0px 0px 0px #000;
        //border: 1px solid var(--info);
        height: 15px;
        width: 15px;
        border-radius: 25px;
        background: var(--info);
        cursor: pointer;
        -webkit-appearance: none;
        margin-top: -6px;
      }

      input[type='range']::-moz-range-thumb {
        z-index: 2;
        position: relative;
        box-shadow: 0px 0px 0px #000;
        height: 15px;
        width: 15px;
        border-radius: 25px;
        background: var(--info);
        cursor: pointer;
        border: 0; // Firefox centra il thumb da solo, niente margin-top
      }
    }

    &-number-input {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    &-slider-input {
      //background-color: red;
      height: 100%;
      width: 100%;
      position: absolute;
      bottom: 0;
      @-moz-document url-prefix() {
        /* Firefox only (autoprefixer can't parse @supports selector()) */
        border-bottom: 3px solid #ddd;
      }
    }

    &-wrapper {
      position: relative;
    }

    &-bars {
      height: 80px;
      display: flex;
      align-items: flex-end;
      padding-left: 6px;
      padding-right: 6px;
    }

    &-bar-wrapper {
      position: relative;
      flex: 1;
      margin: 0 1px 0 0;
      background-color: #ddd;
      margin-bottom: 3px;
      @-moz-document url-prefix() {
        /* Firefox only (autoprefixer can't parse @supports selector()) */
        margin-bottom: 0px;
      }
    }

    &-bar {
      height: 100%;
      width: 100%;
      position: relative;
      &.selected {
        background-color: #bbb;
      }
    }
  }
</style>
